import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { InboxItem } from "@/lib/api";

const SEEN_TS_KEY = "signals_web.inbox.newest_seen_ts";
const HISTORY_KEY = "signals_web.inbox.notification_history";

export interface InboxNotificationEvent {
  id: string;
  sourceId: string;
  symbols: string[];
  body: string;
  pageUrl: string | null;
  itemTimestamp: string;
  receivedAt: string;
}

type NotificationPermissionState =
  | NotificationPermission
  | "unsupported";

export function useInboxNotifications(
  items: InboxItem[],
  onOpenInboxItem?: (inboxItemId: string) => void,
) {
  const [permission, setPermission] = useState<NotificationPermissionState>(
    getNotificationPermission(),
  );
  const [history, setHistory] = useState<InboxNotificationEvent[]>(() =>
    readHistory(),
  );
  const [freshIds, setFreshIds] = useState<Set<string>>(() => new Set());
  const lastBatchRef = useRef<string>("");

  const newestTs = useMemo(() => {
    let newest = 0;
    for (const item of items) {
      newest = Math.max(newest, itemTimeMs(item));
    }
    return newest;
  }, [items]);

  useEffect(() => {
    if (items.length === 0 || newestTs <= 0) return;

    const storedSeen = Number(window.localStorage.getItem(SEEN_TS_KEY) ?? "0");
    if (!Number.isFinite(storedSeen) || storedSeen <= 0) {
      window.localStorage.setItem(SEEN_TS_KEY, String(newestTs));
      return;
    }

    const newItems = items
      .filter((item) => item.status !== "archived" && item.status !== "ignore")
      .filter((item) => itemTimeMs(item) > storedSeen)
      .sort((a, b) => itemTimeMs(b) - itemTimeMs(a));

    if (newItems.length === 0) return;

    const batchKey = newItems.map((item) => item.id).join(",");
    if (batchKey === lastBatchRef.current) return;
    lastBatchRef.current = batchKey;

    window.localStorage.setItem(SEEN_TS_KEY, String(newestTs));
    setFreshIds((prev) => {
      const next = new Set(prev);
      newItems.forEach((item) => next.add(item.id));
      return next;
    });

    const events = newItems.map(toNotificationEvent);
    setHistory((prev) => {
      const merged = mergeHistory(events, prev);
      writeHistory(merged);
      return merged;
    });

    if (getNotificationPermission() === "granted") {
      showBrowserNotification(newItems, onOpenInboxItem);
    }
  }, [items, newestTs, onOpenInboxItem]);

  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      setPermission("unsupported");
      return "unsupported";
    }
    const next = await window.Notification.requestPermission();
    setPermission(next);
    return next;
  }, []);

  const clearHistory = useCallback(() => {
    window.localStorage.removeItem(HISTORY_KEY);
    setHistory([]);
  }, []);

  const deleteHistoryItem = useCallback((inboxItemId: string) => {
    setHistory((prev) => {
      const next = prev.filter((event) => event.id !== inboxItemId);
      writeHistory(next);
      return next;
    });
  }, []);

  const clearFreshInboxItem = useCallback((inboxItemId: string) => {
    setFreshIds((prev) => {
      if (!prev.has(inboxItemId)) return prev;
      const next = new Set(prev);
      next.delete(inboxItemId);
      return next;
    });
  }, []);

  return {
    clearFreshInboxItem,
    clearHistory,
    deleteHistoryItem,
    freshInboxItemIds: freshIds,
    history,
    permission,
    requestPermission,
  };
}

function getNotificationPermission(): NotificationPermissionState {
  if (!("Notification" in window)) return "unsupported";
  return window.Notification.permission;
}

function itemTimeMs(item: InboxItem): number {
  const value = item.captured_at;
  const parsed = value ? new Date(value).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

function itemTimeIso(item: InboxItem): string {
  return item.item_timestamp ?? item.captured_at;
}

function toNotificationEvent(item: InboxItem): InboxNotificationEvent {
  return {
    id: item.id,
    sourceId: item.source_id,
    symbols: item.detected_symbols ?? [],
    body: previewText(item),
    pageUrl: item.page_url,
    itemTimestamp: itemTimeIso(item),
    receivedAt: new Date().toISOString(),
  };
}

function previewText(item: InboxItem): string {
  return (item.raw_text ?? item.markdown ?? "").trim();
}

function mergeHistory(
  incoming: InboxNotificationEvent[],
  existing: InboxNotificationEvent[],
): InboxNotificationEvent[] {
  const byId = new Map<string, InboxNotificationEvent>();
  for (const event of [...incoming, ...existing]) {
    if (!byId.has(event.id)) byId.set(event.id, event);
  }
  return Array.from(byId.values())
    .sort(
      (a, b) =>
        new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime(),
    );
}

function readHistory(): InboxNotificationEvent[] {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeHistory(history: InboxNotificationEvent[]): void {
  window.localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
}

function showBrowserNotification(
  items: InboxItem[],
  onOpenInboxItem?: (inboxItemId: string) => void,
): void {
  const first = items[0];
  if (!first) return;
  const title =
    items.length === 1
      ? `New inbox item from ${first.source_id}`
      : `${items.length} new inbox items`;
  const symbols = (first.detected_symbols ?? []).join(", ");
  const bodyPrefix = symbols ? `${symbols}: ` : "";
  const notification = new window.Notification(title, {
    body: `${bodyPrefix}${previewText(first)}`,
    tag: "signals-new-inbox-items",
  });
  notification.onclick = () => {
    window.focus();
    onOpenInboxItem?.(first.id);
    notification.close();
  };
}
